import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import useRefinementList from 'hooks/useRefinementList';
import FacetSelect from './FacetSelect';

const SUGGESTION_COUNT = 4;

const FacetSelector = ({ search, attribute, placeholder, showSuggestions }) => {
  // Locked-in suggestion values from the first data load — positions never shift
  const fixedSuggestionsRef = useRef(null);

  const { items, refined, refine } = useRefinementList(search, attribute, {
    operator: 'and',
  });

  // Snapshot the top-N on first load so the pill order never changes
  if (fixedSuggestionsRef.current === null && items.length > 0) {
    fixedSuggestionsRef.current = items
      .slice(0, SUGGESTION_COUNT)
      .map((item) => item.value);
  }

  const availableValues = new Set(items.map((i) => i.value));
  const liveSuggestions = (fixedSuggestionsRef.current || []).filter(
    (v) => availableValues.has(v) || refined.includes(v)
  );
  const extraSelected = refined.filter((v) => !liveSuggestions.includes(v));

  const suggestionPills = showSuggestions
    ? [...liveSuggestions, ...extraSelected]
    : [];
  const selectedOnlyPills = !showSuggestions ? refined : [];
  const allPillValues = [
    ...new Set([...suggestionPills, ...selectedOnlyPills]),
  ];

  const pills = allPillValues.map((value) => ({
    key: value,
    label: value,
    isSelected: refined.includes(value),
  }));

  const selectOptions = items
    .filter((item) => !item.isRefined)
    .map((item) => ({ label: item.label, value: item.value }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <FacetSelect
      options={selectOptions}
      pills={pills}
      placeholder={placeholder}
      onSelect={(selected) => selected && refine(selected.value)}
      onRemovePill={(key) => refine(key)}
    />
  );
};

FacetSelector.propTypes = {
  search: PropTypes.object,
  attribute: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  showSuggestions: PropTypes.bool,
};

FacetSelector.defaultProps = {
  placeholder: 'Search…',
  showSuggestions: true,
};

export default FacetSelector;
